/* eslint no-shadow: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withTranslation } from 'react-i18next';
import { titleize, unescapeHTML } from 'underscore.string';

import AuditHistory from 'components/modules/shipments/AuditHistory';
import DataPoller from 'components/ui/DataPoller';
import CustomerStore from 'stores/CustomerSearchStore';
import CustomerActionCreators from 'actions/CustomersActionCreators';
import LabelValue from 'components/ui/tailwind/label-value';
import AccountContacts from 'components/v2/contacts';

import RecentActivity from 'components/modules/user/recent-activity';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';

import { stateCodes } from 'components/modules/user/user-form-utils';
import {
  getAddressString,
  hasAddress,
} from 'utils/transformers/AddressTransformer';
import DriverDocuments from './DriverDocuments';
import Capability from '../Capability';
import EditButton from '../../../v2/buttons/EditButton';
import Badge from '../../../v2/Badge';
import { Avatar } from '../Avatar';

const POLLING_FREQ = 10000;

const REQUIRED_ATTRIBUTES = [
  'first_name',
  'last_name',
  'username',
  'phone',
  'email',
  'street',
  'city',
  'state',
  'zipcode',
];

const getRequiredAsterisk = (field) => {
  return REQUIRED_ATTRIBUTES.includes(field) ? '*' : '';
};

class DriverDetails extends Component {
  static propTypes = {
    details: PropTypes.object,
    // onAuthorize: PropTypes.func,
    onOpenJobs: PropTypes.func.isRequired,
    // onOpenPassword: PropTypes.func.isRequired,
    onSaveDetails: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showAccountModal: false,
      category: null,
      driver: props.details,
      audits: [],
      errors: {
        first_name: {},
        last_name: {},
        username: {},
        cdl: {},
        phone: {},
        email: {},
        address: {},
        authorized: {},
        tsa: {},
        residential: {},
        test: {},
      },
      changed: {},
    };
  }

  componentDidMount() {
    CustomerStore.addAuditChangeListener(this.onAuditChange);
  }

  componentWillUnmount() {
    CustomerStore.removeAuditChangeListener(this.onAuditChange);
  }

  isRequiredField = () => `${this.props.t('IS_REQUIRED_FIELD')}`;

  onAuditChange = () => {
    const audits = CustomerStore.getCustomerAudits();
    if (!_.isEqual(audits, this.state.audits)) {
      return this.setState({ audits });
    }
  };

  errorsExist() {
    const { errors } = this.state;
    let errorsExist = false;
    _.map(errors, (value, key) => {
      if (!_.isEmpty(errors[key])) errorsExist = true;
    });
    return errorsExist;
  }

  onCarrierProfileRedirect = () => {
    const { driver } = this.state;
    const carrierId = driver.carrier._id || driver.carrier;
    window.location.href = `/users/${carrierId}`;
  };

  onSave = () => {
    const { changed, driver } = this.state;
    if (this.state.category === 'address') {
      if (
        !this.areRequiredFieldsPresent(driver.address, [
          'street',
          'street2',
          'city',
          'state',
          'zipcode',
        ])
      ) {
        return;
      }
    }
    // @TODO maybe refactor this...
    _.map(changed, (item, key) => {
      if (!['badges', 'address', 'test'].includes(key)) {
        if (driver[key] && driver[key] instanceof String) {
          driver[key] = driver[key].trim();
        }
      }

      if (key === 'address') {
        const address = driver.address;
        _.map(driver.address, (item, key) => {
          address[key] = address[key].trim();
        });
        driver.address = address;
      }
    });
    const updates = _.pick(driver, Object.keys(changed));
    updates._id = driver._id; // Didn't change but needed for the PATCH

    this.props.onSaveDetails('Driver', updates);
  };

  getUserAudits = () => {
    CustomerActionCreators.fetchCustomerAudits(this.props.details._id);
  };

  areRequiredFieldsPresent = (parent, fields) => {
    const { t } = this.props;
    let errors;
    this.setState((prevState) => {
      errors = prevState.errors;
      fields.forEach((field) => {
        errors[field] =
          !parent[field] && REQUIRED_ATTRIBUTES.includes(field)
            ? {
                message: `${t(field.toUpperCase())} ${this.isRequiredField()}`,
              }
            : {};
      });
      return { ...prevState, errors };
    });
    const allPresent = _.every(fields, (f) => _.isEmpty(errors[f]));
    return allPresent;
  };

  handleAddressFieldChange = (field, value) => {
    const { t } = this.props;
    const { driver, changed, errors } = this.state;
    const address = driver.address;

    address[field] = value;
    driver.address = address;
    changed.address = true;
    errors[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? {
            message: `${t(field.toUpperCase())} ${this.isRequiredField()}`,
          }
        : {};

    this.setState({ driver, changed, errors });
  };

  handleBadgeFieldChange = (field, value) => {
    const { t } = this.props;
    const { driver, changed, errors } = this.state;
    let badges = driver.badges || [];

    if (value === false) {
      badges = badges.filter((badge) => {
        return badge.type !== field;
      });
    } else if (!_.findWhere(badges, { type: field })) {
      badges.push({ type: field });
    }

    driver.badges = badges;
    changed.badges = true;
    errors[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? {
            message: `${t(field.toUpperCase())} ${this.isRequiredField()}`,
          }
        : {};

    this.setState({ driver, changed, errors });
  };

  handleFieldChange = (field, value) => {
    const { t } = this.props;
    const { driver, changed, errors } = this.state;
    driver[field] = value;
    changed[field] = true;

    errors[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? {
            message: `${t(field.toUpperCase())} ${this.isRequiredField()}`,
          }
        : {};

    this.setState({ driver, changed, errors });
  };

  renderAddress() {
    const {
      driver: { address },
    } = this.state;
    const addressString = getAddressString(address);
    return <LabelValue value={addressString} icon="map-marker" />;
  }

  renderAccountModal = () => {
    const { errors, driver } = this.state;
    const {
      first_name,
      last_name,
      username,
      cdl,
      phone,
      email,
      authorized,
      test,
      address: { street, street2, city, state, zipcode },
      badges,
    } = driver;

    const tsa = _.findWhere(badges, { type: 'tsa' }) != null;
    const residential = _.findWhere(badges, { type: 'residential' }) != null;
    const { category } = this.state;
    const { t } = this.props;

    return (
      <div>
        {category === 'account' && (
          <div className="grid sm:grid-cols-6 sm:gap-4 gap-y-6">
            <CargomaticInput
              className="sm:col-span-3"
              error={errors.first_name}
              label={`${t('FIRST_NAME')} ${getRequiredAsterisk('first_name')}`}
              info={t('DRIVERS_FIRST_NAME')}
              onChange={(value) => this.handleFieldChange('first_name', value)}
              value={first_name}
            />
            <CargomaticInput
              className="sm:col-span-3"
              error={errors.last_name}
              label={`${t('LAST_NAME')} ${getRequiredAsterisk('last_name')}`}
              info={t('DRIVERS_LAST_NAME')}
              onChange={(value) => this.handleFieldChange('last_name', value)}
              value={last_name}
            />
            <CargomaticInput
              className="sm:col-span-3"
              error={errors.phone}
              label={`${t('PHONE_NUMBER')} ${getRequiredAsterisk('phone')}`}
              info={t('DRIVERS_PHONE_NUMBER')}
              onChange={(value) => this.handleFieldChange('phone', value)}
              value={phone}
            />
            <CargomaticInput
              className="sm:col-span-3"
              error={errors.username}
              label={`${t('USERNAME')} ${getRequiredAsterisk('username')}`}
              info={t('DRIVER_USERNAME')}
              onChange={(value) => this.handleFieldChange('username', value)}
              value={username}
            />
            <CargomaticInput
              className="sm:col-span-4"
              error={errors.email}
              label={`${t('EMAIL_ADDRESS')} ${getRequiredAsterisk('email')}`}
              info={t('DRIVER_EMAIL_ADDRESS')}
              onChange={(value) => this.handleFieldChange('email', value)}
              value={email}
              maxLength={254}
            />
            <CargomaticInput
              className="sm:col-span-3"
              error={errors.cdl}
              label={`${t('COMMERCIAL_DRIVERS_LICENSE')} ${getRequiredAsterisk(
                'cdl',
              )}`}
              info={t('COMMERCIAL_DRIVER_LICENSE_TIP')}
              onChange={(value) => this.handleFieldChange('cdl', value)}
              value={cdl}
            />
          </div>
        )}
        {category === 'address' && (
          <div>
            <h3 className="mt-0 mb-4">{t('ACCOUNT_INFORMATION')}</h3>
            <div className="grid sm:grid-cols-3 sm:gap-4 gap-y-6">
              <CargomaticInput
                className="sm:col-span-full"
                error={errors.street}
                label={`${titleize(t('STREET'))} ${getRequiredAsterisk(
                  'street',
                )}`}
                info={t('DRIVER_STREET')}
                onChange={(value) => {
                  this.handleAddressFieldChange('street', value);
                }}
                value={street}
              />
              <CargomaticInput
                className="sm:col-span-full"
                error={errors.street2}
                label={`${titleize(t('STREET_2'))} ${getRequiredAsterisk(
                  'street2',
                )}`}
                info={t('DRIVER_STREET_2')}
                onChange={(value) => {
                  this.handleAddressFieldChange('street2', value);
                }}
                value={street2}
              />
              <CargomaticInput
                error={errors.city}
                label={`${titleize(t('CITY'))} ${getRequiredAsterisk('city')}`}
                info={t('DRIVER_CITY')}
                onChange={(value) => {
                  this.handleAddressFieldChange('city', value);
                }}
                value={city}
              />
              <CargomaticSelect
                error={errors.state}
                selected={state}
                info={t('DRIVER_STATE')}
                items={stateCodes}
                label={`${titleize(t('STATE'))} ${getRequiredAsterisk(
                  'state',
                )}`}
                valueKey="abbreviation"
                textKey="name"
                onSelect={(item) => {
                  this.handleAddressFieldChange('state', item.abbreviation);
                }}
              />
              <CargomaticInput
                error={errors.zipcode}
                label={`${t('ZIP_CODE')} ${getRequiredAsterisk('zipcode')}`}
                info={t('DRIVER_POSTAL_CODE')}
                onChange={(value) => {
                  this.handleAddressFieldChange('zipcode', value);
                }}
                value={zipcode}
              />
            </div>
          </div>
        )}
        {category === 'capabilities' && (
          <div>
            <h3 className="mt-0 mb-4">{t('CAPABILITIES')}</h3>
            <div className="grid sm:grid-cols-2 sm:gap-x-4 gap-y-2">
              <CargomaticInput
                id="authorized"
                type="checkbox"
                error={errors.authorized}
                label={`${titleize(t('AUTHORIZED'))} ${getRequiredAsterisk(
                  'authorized',
                )}`}
                info={t('DRIVER_AUTHORIZATION_STATUS')}
                onChange={(value) => {
                  this.handleFieldChange('authorized', value);
                }}
                value={authorized}
              />
              <CargomaticInput
                id="tsa"
                type="checkbox"
                error={errors.tsa}
                label={`${t('TSA')} ${getRequiredAsterisk('tsa')}`}
                info={t('DRIVER_TSA_CERTIFICATION_STATUS')}
                onChange={(value) => this.handleBadgeFieldChange('tsa', value)}
                value={tsa}
              />
              <CargomaticInput
                id="residential"
                type="checkbox"
                error={errors.residential}
                label={`${t('RESIDENTIAL')} ${getRequiredAsterisk(
                  'residential',
                )}`}
                info={t('DRIVER_RESIDENTIAL_STATUS')}
                onChange={(value) => {
                  this.handleBadgeFieldChange('residential', value);
                }}
                value={residential}
              />
              <CargomaticInput
                id="test"
                type="checkbox"
                error={errors.test}
                label={`${t('TEST_USER')} ${getRequiredAsterisk('test')}`}
                info={t('DRIVER_TEST_USER_STATUS')}
                onChange={(value) => this.handleFieldChange('test', value)}
                value={test}
              />
            </div>
          </div>
        )}
        <div className="mt-4 form-actions">
          <button
            className="button button-green"
            onClick={this.onSave}
            disabled={this.errorsExist()}
          >
            {t('SAVE')}
          </button>
        </div>
      </div>
    );
  };

  render() {
    const { driver } = this.state;
    const { badges, authorized, carrier } = driver;
    const { t } = this.props;

    const tsa = _.findWhere(badges, { type: 'tsa' }) != null;
    const residential = _.findWhere(badges, { type: 'residential' }) != null;
    return (
      <div className="mx-auto my-16 max-w-screen-2xl md:my-32 lg:my-16">
        <DataPoller action={this.getUserAudits} frequency={POLLING_FREQ} />
        <div className="lg:grid lg:grid-cols-4 lg:gap-6">
          <div className="col-span-1">
            <CargomaticCard className="mb-6">
              <div className="text-center">
                <div className="flex justify-center py-6">
                  <Avatar
                    key={driver._id}
                    userId={driver._id}
                    className="h-32 w-32 cursor-pointer rounded-full"
                  />
                </div>
                <div className="flex flex-wrap justify-center items-center gap-x-2 gap-y-2 ">
                  <Badge color="blue">{t('DRIVER')}</Badge>
                  {authorized ? (
                    <Badge color="green">{t('DRIVER_AUTHORIZED')}</Badge>
                  ) : (
                    <Badge color="red">{t('DRIVER_UNAUTHORIZED')}</Badge>
                  )}
                  {carrier.authorized ? (
                    <Badge color="green">{t('CARRIER_AUTHORIZED')}</Badge>
                  ) : (
                    <Badge color="red">{t('CARRIER_UNAUTHORIZED')}</Badge>
                  )}
                </div>

                <h2 className="text-grey-darker">{`${driver.first_name} ${driver.last_name}`}</h2>
                <a
                  className="cursor-pointer text-grey-darker"
                  onClick={this.onCarrierProfileRedirect}
                >
                  {driver.carrier && unescapeHTML(driver.carrier.name)}
                </a>
                <button
                  className="button button-secondary text-grey-darker"
                  onClick={this.props.onOpenJobs}
                >
                  {t('VIEW_JOBS')}
                </button>
              </div>
            </CargomaticCard>
            <CargomaticCard
              className="mb-6"
              header={() => (
                <header
                  onClick={() => {
                    this.setState({
                      category: 'account',
                      showAccountModal: true,
                    });
                  }}
                  className="flex items-center justify-between"
                >
                  <h3 className="my-0">{t('ACCOUNT_DETAILS')}</h3>
                  <EditButton />
                </header>
              )}
            >
              <LabelValue
                value={`${driver.first_name} ${driver.last_name}`}
                icon="user"
              />
              <LabelValue value={driver.username} icon="lock" />
              <LabelValue value={driver.phone} icon="phone" />
              <LabelValue value={driver.cdl} icon="envelope" />
            </CargomaticCard>
            <div className="mt-6">
              <button
                className="w-full button button-hollow-red"
                onClick={this.props.onChangePasswordClick}
              >
                {t('CHANGE_PASSWORD')}
              </button>
            </div>
          </div>
          <div className="col-span-2">
            <div className="mb-6">
              <RecentActivity email={driver.email} />
            </div>

            <CargomaticCard
              className="mb-6"
              header={() => <h3 className="my-0">{t('DOCUMENTS')}</h3>}
            >
              <DriverDocuments id={driver._id} />
            </CargomaticCard>
            <CargomaticCard
              className="mb-6"
              header={() => <h3 className="my-0">{t('AUDIT_HISTORY')}</h3>}
            >
              <AuditHistory audits={this.state.audits} paginate />
            </CargomaticCard>
            <CargomaticCard>
              <AccountContacts contacts={driver.contacts} userId={driver._id} />
            </CargomaticCard>
          </div>
          <div className="col-span-1">
            <CargomaticCard
              className="mb-6"
              header={() => (
                <header
                  onClick={() => {
                    this.setState({
                      category: 'address',
                      showAccountModal: true,
                    });
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="my-0">{t('ADDRESS_DETAILS')}</h3>
                    <EditButton />
                  </div>
                </header>
              )}
            >
              {hasAddress(driver.address) && this.renderAddress(driver.address)}
              {!hasAddress(driver.address) && (
                <LabelValue value={t('NO_ADDRESS_ON_FILE')} icon="map-marker" />
              )}
            </CargomaticCard>
            <CargomaticCard
              className="mb-6"
              header={() => (
                <header
                  onClick={() => {
                    this.setState({
                      category: 'capabilities',
                      showAccountModal: true,
                    });
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="my-0">{t('CAPABILITIES')}</h3>
                    <EditButton />
                  </div>
                </header>
              )}
            >
              <Capability
                label={titleize(t('AUTHORIZED'))}
                checked={driver.authorized}
              />
              <Capability label={t('TSA')} checked={tsa} />
              <Capability label={t('RESIDENTIAL')} checked={residential} />
              <Capability label={t('TEST')} checked={residential} />
            </CargomaticCard>
          </div>
        </div>
        {this.state.showAccountModal && (
          <CargomaticModal
            show
            onHide={() => this.setState({ showAccountModal: false })}
            title={t('ACCOUNT_INFORMATION')}
            body={this.renderAccountModal()}
            footer={null}
            location="middle"
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(DriverDetails);
